import React, {useEffect, useState} from 'react'
import {useSearchParams} from 'react-router-dom'
import {toast} from 'react-toastify'
import {PurchaseActionButton} from '../modules/purchases/PurchaseActionButton'
import {missingCatalogModules} from '../modules/purchases/catalog'
import {QuotePurchaseKind, QuoteRequestDialog} from '../modules/purchases/QuoteRequestDialog'
import {RechargeDialog} from '../modules/purchases/RechargeDialog'
import {SessionPreferences} from '../modules/security-settings/SessionPreferences'
import {SmsMfaAccountRow} from '../modules/security-settings/SmsMfaAccountRow'
import {TotpMfaAccountRow} from '../modules/security-settings/TotpMfaAccountRow'
import {useCognitoAuth} from '../modules/auth'
import {useSubscription} from '../../shared/utils'
import {ManageUsersSection} from '../modules/membership/ManageUsersSection'
import {LinkedMembershipChooser} from '../modules/membership/LinkedMembershipChooser'
import {ApiKeysSection} from '../modules/api-keys/ApiKeysSection'
import {ConnectedAppsSection} from '../modules/connect/ConnectedAppsSection'
import {SharingSettingsSection} from '../modules/sharing/SharingSettingsSection'
import {CustomModal} from '../modules/widgets/components/CustomModal'
import {MembershipSubscription} from '../modules/membership/api'
import {getLogger} from '../../shared/utils/logUtils'
import {getUsableMemberships} from '../../shared/utils/membershipActivation'
import {toastDefaultConfig} from '../../shared/config/toastConfig'
import {
  EXPIRED_GRACE_DAYS,
  getGracePeriodEnd,
  isSubscriptionExpired,
} from '../../shared/utils/subscriptionLifecycle'

/** Suffix: role, plus evaluation / expired / inactive; omit plain "active" (only active keys are listed). */
const roleAndStatusForOption = (sub: MembershipSubscription): string => {
  if (sub.is_expired) {
    return ` (${sub.role}, expired)`
  }
  if (sub.is_enabled === false) {
    return ` (${sub.role}, inactive)`
  }
  if (sub.is_evaluation) {
    return ` (${sub.role}, evaluation)`
  }
  return ` (${sub.role})`
}

const formatSubscriptionOption = (sub: MembershipSubscription): string => {
  const product = sub.product || 'Unknown product'
  return `${product} — ${sub.subscription_id}${roleAndStatusForOption(sub)}`
}

/**
 * When the key is hidden, show a prefix from the start of the id, then a separator, then
 * the last 4 characters (so the end is not the only visible segment).
 */
const maskSubscriptionIdForDisplay = (id: string): string => {
  if (!id) {
    return '—'
  }
  if (id.length <= 4) {
    return '·'.repeat(id.length)
  }
  if (id.length <= 8) {
    return `${'·'.repeat(id.length - 4)}${id.slice(-4)}`
  }
  if (id.length <= 20) {
    return `${id.slice(0, id.length - 4 - 1)}·${id.slice(-4)}`
  }
  return `${id.slice(0, 12)}…${id.slice(-4)}`
}

export const PreferencesPage: React.FC = () => {
  const {currentUser} = useCognitoAuth()
  const {
    subscriptionId,
    subscriptionDetails,
    isLoadingDetails,
    balance,
    linkedSubscriptions,
    isSwitching,
    switchSubscription,
    discoverSubscriptions,
    isDiscovering,
    reloadSubscriptionDetails,
    role,
    subscriptionLifecycle,
  } = useSubscription()
  // Expired (in or past the grace period): only Renew is offered, add-ons are hidden.
  const isExpired = isSubscriptionExpired(subscriptionLifecycle)
  const [searchParams, setSearchParams] = useSearchParams()
  const [showRecharge, setShowRecharge] = useState(false)
  const [quoteKind, setQuoteKind] = useState<QuotePurchaseKind | null>(null)
  const [checkoutFeedback, setCheckoutFeedback] = useState<'success' | 'cancel' | null>(null)
  const [showSubscriptionKey, setShowSubscriptionKey] = useState<boolean>(false)
  const [pendingSwitch, setPendingSwitch] = useState<MembershipSubscription | null>(null)
  const [isLinkingSubscription, setIsLinkingSubscription] = useState(false)

  // Re-run subscription discovery every time the Settings page mounts, so that
  // navigating here (or refreshing the page) always picks up newly purchased
  // keys. The provider also runs discovery once right after login for silent
  // auto-link — this mount-level trigger is the user-visible refresh point.
  useEffect(() => {
    if (!currentUser) return
    void (async () => {
      const result = await discoverSubscriptions()
      if (result.warning) {
        toast.error(
          'We couldn\'t check the subscription system right now. Please try again in a moment.',
          toastDefaultConfig
        )
      }
      // Success paths (found, auto-linked, or nothing found) are either
      // surfaced by the provider-mounted modal or intentionally silent.
    })()
    // discoverSubscriptions is not stable across renders; we explicitly want
    // this effect to fire once per mount when currentUser is set.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser])

  const checkoutResult = searchParams.get('checkout')
  useEffect(() => {
    if (checkoutResult !== 'success' && checkoutResult !== 'cancel') return
    setCheckoutFeedback(checkoutResult)
    const next = new URLSearchParams(searchParams)
    next.delete('checkout')
    next.delete('session_id')
    setSearchParams(next, {replace: true})
  }, [checkoutResult, searchParams, setSearchParams])

  // `/add-units` redirects here with ?recharge=open; open the dialog once and drop the param so a
  // refresh or closing the dialog does not reopen it.
  const rechargeRequested = searchParams.get('recharge') === 'open'
  useEffect(() => {
    if (!rechargeRequested) return
    setShowRecharge(true)
    const next = new URLSearchParams(searchParams)
    next.delete('recharge')
    setSearchParams(next, {replace: true})
  }, [rechargeRequested, searchParams, setSearchParams])

  const handleRechargeCloud = () => {
    setShowRecharge(true)
  }

  // Manual refresh (button click) re-runs discovery and surfaces explicit
  // feedback toasts for the "nothing found" and success paths too — the
  // mount-time auto-trigger above only surfaces the warning toast, so the
  // two paths diverge intentionally.
  const handleRefreshSubscriptions = async () => {
    const result = await discoverSubscriptions()
    if (result.warning) {
      toast.error(
        'We couldn\'t check the subscription system right now. Please try again in a moment.',
        toastDefaultConfig
      )
      return
    }
    if (result.autoLinked > 0) {
      toast.success(
        `Linked ${result.autoLinked} subscription${result.autoLinked === 1 ? '' : 's'} to your account.`,
        toastDefaultConfig
      )
      return
    }
    if (result.found === 0) {
      toast.info('No additional subscriptions found for your account.', toastDefaultConfig)
    }
    // result.found > 0 && autoLinked === 0 → provider populates
    // discoveredSubscriptions and the modal opens automatically.
  }

  const handleSubscriptionSelect = (skey: string) => {
    const target = linkedSubscriptions.find((s) => s.skey === skey)
    if (!target || target.is_primary) return
    setPendingSwitch(target)
  }

  const handleConfirmSwitch = async () => {
    if (!pendingSwitch) return
    const target = pendingSwitch
    setIsLinkingSubscription(true)
    try {
      await switchSubscription(target.skey)
      setPendingSwitch(null)
      toast.success(
        `Now linked: ${target.product || 'subscription'} — ${target.subscription_id}`,
        toastDefaultConfig
      )
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Failed to link subscription'
      toast.error(msg, toastDefaultConfig)
      getLogger().error('Switch subscription failed:', err)
    } finally {
      setIsLinkingSubscription(false)
    }
  }

  if (!currentUser) {
    return (
      <div className='ob-page ob-compact-controls' style={{paddingTop: 'var(--ob-spacing-3)'}}>
        <div className='card-header border-0 pt-3' style={{minHeight: '80px'}}>
          <div style={{display: 'flex', flexDirection: 'column', alignItems: 'start'}}>
            <h3>Settings</h3>
            <h5 style={{color: 'grey', alignContent: 'center'}}>
              Manage your account settings and preferences
            </h5>
          </div>
        </div>
        <div className='ob-page__section'>
          <div className='alert alert-warning'>
            <i className='fas fa-exclamation-triangle me-2'></i>
            You need to be logged in to access your preferences.
          </div>
        </div>
      </div>
    )
  }

  const primarySubscription = linkedSubscriptions.find((s) => s.is_primary)
  const selectedSkey = primarySubscription?.skey ?? ''
  const hasMultipleSubscriptions = linkedSubscriptions.length > 1
  // Memberships of a user with no active subscription; one is activated by the provider, several
  // are offered here.
  const selectableMemberships = subscriptionId ? [] : getUsableMemberships(linkedSubscriptions)

  return (
    <div className='ob-page ob-compact-controls' style={{paddingTop: 'var(--ob-spacing-3)'}}>
      {showRecharge && (
        <RechargeDialog
          closeModal={() => setShowRecharge(false)}
          role={role}
          subscriptionId={subscriptionId}
        />
      )}
      {quoteKind && (
        <QuoteRequestDialog
          kind={quoteKind}
          subscriptionId={subscriptionId}
          ownedModules={subscriptionDetails?.modules}
          closeModal={() => setQuoteKind(null)}
        />
      )}
      {checkoutFeedback === 'success' && (
        <CustomModal
          title='Payment successful'
          titleIcon='fas fa-check-circle'
          closeModal={() => setCheckoutFeedback(null)}
          buttons={[
            {
              text: 'Close',
              className: 'btn btn-primary',
              onClick: () => setCheckoutFeedback(null),
            },
          ]}
        >
          <p className='mb-0'>
            Your payment was successful. Cloud Units will be applied to this subscription shortly.
          </p>
        </CustomModal>
      )}
      {checkoutFeedback === 'cancel' && (
        <CustomModal
          title='Payment cancelled'
          titleIcon='fas fa-times-circle'
          closeModal={() => setCheckoutFeedback(null)}
          buttons={[
            {
              text: 'Close',
              className: 'btn btn-primary',
              onClick: () => setCheckoutFeedback(null),
            },
          ]}
        >
          <p className='mb-0'>No charge was made. You can try again whenever you are ready.</p>
        </CustomModal>
      )}
      {/* Header */}
      <div className='card-header border-0 pt-3' style={{minHeight: '80px'}}>
        <div style={{display: 'flex', flexDirection: 'column', alignItems: 'start'}}>
          <h3>Settings</h3>
          <h5 style={{color: 'grey', alignContent: 'center'}}>
            Manage your account settings and preferences
          </h5>
        </div>
      </div>

      <div className='row g-5 ob-page__settings-row'>
        {/* Left Column - Subscription Details */}
        <div className='col-lg-6 d-flex'>
          <div className='ob-page__section d-flex flex-column flex-grow-1'>
            <h3 className='ob-page__section-title'><i className='fas fa-clipboard-list me-2' />Subscription Details</h3>

            <p className='ob-text--muted ob-text--small mb-3'>
              Details about your current OmicsBox subscription plan and licensed modules
            </p>

            {isLoadingDetails ? (
              <div className='card flex-grow-1'>
                <div className='card-body text-center py-4'>
                  <span className='spinner-border spinner-border-sm me-2' role='status' aria-hidden='true'></span>
                  Loading subscription details...
                </div>
              </div>
            ) : subscriptionDetails ? (
              <div className='card flex-grow-1'>
                <div className='card-body py-0 pt-0 px-3 pb-3'>
                  <div className='ob-page__field mb-2 pt-0'>
                    <label className='ob-page__field-label'>
                      Subscription key{' '}
                      <span className='text-muted' style={{fontSize: '0.9em', fontWeight: 400}}>
                        (linked)
                      </span>
                    </label>
                    <div className='ob-page__field-value d-flex align-items-center justify-content-between gap-2'>
                      {hasMultipleSubscriptions ? (
                        <div className='d-flex align-items-center gap-2 flex-grow-1 min-w-0'>
                          <select
                            className='form-select form-select-sm text-break'
                            value={selectedSkey}
                            onChange={(e) => handleSubscriptionSelect(e.target.value)}
                            disabled={isSwitching}
                            aria-label='Select which linked subscription to use'
                          >
                            {linkedSubscriptions.map((sub) => (
                              <option key={sub.skey} value={sub.skey}>
                                {formatSubscriptionOption(sub)}
                              </option>
                            ))}
                          </select>
                          {isSwitching && (
                            <span
                              className='spinner-border spinner-border-sm text-primary'
                              role='status'
                              aria-hidden='true'
                            />
                          )}
                        </div>
                      ) : (
                        <div className='d-flex align-items-center gap-2 flex-grow-1 min-w-0'>
                          <code
                            className='text-break'
                            style={{fontSize: '0.85em', wordBreak: 'break-all'}}
                          >
                            {showSubscriptionKey
                              ? subscriptionId
                              : subscriptionId
                                ? maskSubscriptionIdForDisplay(subscriptionId)
                                : '—'}
                          </code>
                          {subscriptionId && (
                            <button
                              className='btn btn-sm btn-outline-secondary py-0 px-1'
                              onClick={() => setShowSubscriptionKey(!showSubscriptionKey)}
                              title={showSubscriptionKey ? 'Hide key' : 'Show key'}
                            >
                              <i
                                className={`fas fa-${showSubscriptionKey ? 'eye-slash' : 'eye'}`}
                                style={{fontSize: '0.75em'}}
                              />
                            </button>
                          )}
                        </div>
                      )}
                      <button
                        type='button'
                        className='btn btn-sm btn-outline-primary py-0 px-2 flex-shrink-0'
                        onClick={handleRefreshSubscriptions}
                        disabled={isDiscovering}
                        title='Check your account for additional subscriptions'
                      >
                        {isDiscovering ? (
                          <>
                            <span
                              className='spinner-border spinner-border-sm me-1'
                              role='status'
                              aria-hidden='true'
                              style={{width: '0.75em', height: '0.75em'}}
                            />
                            <span style={{fontSize: '0.8em'}}>Checking...</span>
                          </>
                        ) : (
                          <>
                            <i className='fas fa-sync-alt me-1' style={{fontSize: '0.75em'}} />
                            <span style={{fontSize: '0.8em'}}>Refresh</span>
                          </>
                        )}
                      </button>
                    </div>
                  </div>

                  <hr />

                  <div className='row mb-3'>
                    <div className='col-md-6'>
                      <div className='ob-page__field'>
                        <label className='ob-page__field-label'>Product</label>
                        <div className='ob-page__field-value'>
                          {subscriptionDetails.productName || 'N/A'}
                        </div>
                      </div>
                    </div>
                    <div className='col-md-6'>
                      <div className='ob-page__field'>
                        <label className='ob-page__field-label'>Status</label>
                        <div className='ob-page__field-value'>
                          {isExpired ? (
                            <span className='badge bg-danger'>Expired</span>
                          ) : subscriptionDetails.isEvaluation ? (
                            <span className='badge bg-warning text-dark'>Evaluation</span>
                          ) : subscriptionDetails.isEnabled ? (
                            <span className='badge' style={{backgroundColor: '#1b2c67'}}>Active</span>
                          ) : (
                            <span className='badge bg-danger'>Inactive</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {isExpired && (
                    <div
                      className={`alert ${subscriptionLifecycle === 'lapsed' ? 'alert-danger' : 'alert-warning'} py-2 small`}
                      role='status'
                      data-testid='subscription-expiry-notice'
                    >
                      <i className='fas fa-calendar-xmark me-2' aria-hidden='true' />
                      {subscriptionLifecycle === 'lapsed'
                        ? `This subscription expired more than ${EXPIRED_GRACE_DAYS} days ago. My Files and the OmicsAI Agent are disabled until you renew.`
                        : `This subscription has expired. My Files stay available and the OmicsAI Agent is read-only until ${
                            getGracePeriodEnd(subscriptionDetails.endDate)?.toLocaleDateString() ?? 'the grace period ends'
                          }. Renew to restore full access.`}
                    </div>
                  )}

                  <div className='row mb-3'>
                    <div className='col-md-6'>
                      <div className='ob-page__field'>
                        <label className='ob-page__field-label'>Activation Date</label>
                        <div className='ob-page__field-value'>
                          {subscriptionDetails.activationDate || 'N/A'}
                        </div>
                      </div>
                    </div>
                    <div className='col-md-6'>
                      <div className='ob-page__field'>
                        <label className='ob-page__field-label'>End Date</label>
                        <div className='ob-page__field-value d-flex align-items-center justify-content-between gap-2'>
                          <span>
                            {subscriptionDetails.endDate || 'N/A'}
                            {subscriptionDetails.duration && (
                              <span className='text-muted ms-2'>({subscriptionDetails.duration} days)</span>
                            )}
                          </span>
                          <PurchaseActionButton
                            label='Renew'
                            title='Renew this subscription'
                            onClick={() => setQuoteKind('renew')}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <hr />

                  <div className='ob-page__field mb-3'>
                    <label className='ob-page__field-label'>Licensed Modules</label>
                    <div className='ob-page__field-value d-flex align-items-center justify-content-between gap-2'>
                      {subscriptionDetails.modules.length > 0 ? (
                        <div className='d-flex flex-wrap gap-2 mt-1'>
                          {subscriptionDetails.modules.map((mod) => (
                            <span key={mod} className='badge bg-primary'>
                              {mod}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span>No modules available</span>
                      )}
                      {!isExpired && missingCatalogModules(subscriptionDetails.modules).length > 0 && (
                        <PurchaseActionButton
                          label='Add Modules'
                          title='Add licensed modules'
                          onClick={() => setQuoteKind('add_module')}
                        />
                      )}
                    </div>
                  </div>

                  <hr />

                  <div className='ob-page__field mb-0'>
                    <label className='ob-page__field-label'>Cloud Computation Units</label>
                    <div className='ob-page__field-value d-flex align-items-center justify-content-between gap-2'>
                      <strong className='text-success'>
                        {balance !== null ? `${balance.toLocaleString()} units` : '—'}
                      </strong>
                      {!isExpired && (
                        <PurchaseActionButton
                          label='Add Units'
                          title='Add cloud computation units'
                          onClick={handleRechargeCloud}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className='card flex-grow-1'>
                <div className='card-body text-center py-4 text-muted'>
                  <p className='mb-3'>
                    <i className='fas fa-info-circle me-2' aria-hidden='true'></i>
                    {subscriptionId
                      ? 'Subscription details could not be loaded. You can try again, or check your network connection.'
                      : selectableMemberships.length > 0
                        ? 'No subscription is active for your account yet.'
                        : linkedSubscriptions.length > 0
                          ? 'The subscriptions your account belongs to have expired or are inactive. Please contact your subscription owner, or check for other subscriptions tied to your account.'
                          : 'Subscription details unavailable. Please ensure your subscription key is linked, or check for subscriptions tied to your account.'}
                  </p>
                  <LinkedMembershipChooser
                    memberships={selectableMemberships}
                    onChoose={handleSubscriptionSelect}
                    disabled={isSwitching || isLinkingSubscription}
                  />
                  <div className='d-flex flex-wrap justify-content-center gap-2'>
                    {subscriptionId ? (
                      <button
                        type='button'
                        className='btn btn-sm btn-outline-primary'
                        onClick={() => void reloadSubscriptionDetails()}
                        disabled={isLoadingDetails || isDiscovering}
                      >
                        {isLoadingDetails ? (
                          <>
                            <span
                              className='spinner-border spinner-border-sm me-1'
                              role='status'
                              aria-hidden='true'
                              style={{width: '0.85em', height: '0.85em'}}
                            />
                            Loading…
                          </>
                        ) : (
                          <>
                            <i className='fas fa-sync-alt me-1' aria-hidden='true'></i>
                            Reload subscription details
                          </>
                        )}
                      </button>
                    ) : (
                      <>
                        <button
                          type='button'
                          className='btn btn-sm btn-outline-primary'
                          onClick={() => void handleRefreshSubscriptions()}
                          disabled={isDiscovering || isLoadingDetails}
                        >
                          {isDiscovering ? (
                            <>
                              <span
                                className='spinner-border spinner-border-sm me-1'
                                role='status'
                                aria-hidden='true'
                                style={{width: '0.85em', height: '0.85em'}}
                              />
                              Checking…
                            </>
                          ) : (
                            <>
                              <i className='fas fa-search me-1' aria-hidden='true'></i>
                              Check for subscriptions
                            </>
                          )}
                        </button>
                        <button
                          type='button'
                          className='btn btn-sm btn-light'
                          onClick={() => void reloadSubscriptionDetails()}
                          disabled={isLoadingDetails || isDiscovering}
                          title='Re-read your linked subscription from the account service'
                        >
                          <i className='fas fa-redo me-1' aria-hidden='true'></i>
                          Reload from account
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right Column - Manage Users */}
        {subscriptionId && (
          <div className='col-lg-6 d-flex'>
            <ManageUsersSection key={subscriptionId} subscriptionId={subscriptionId} />
          </div>
        )}
      </div>

      <div className='row g-5 mt-5 ob-page__settings-row'>
        <div className='col-12 col-lg-6'>
          <div className='ob-page__section'>
            <h3 className='ob-page__section-title'>
              <i className='fas fa-shield-halved me-2' />
              Security &amp; session
            </h3>
            <p className='ob-text--muted ob-text--small ob-spacing--mb-4'>
              Manage two-factor authentication and session duration for your account.
            </p>
            <TotpMfaAccountRow />
            <SmsMfaAccountRow />
            <SessionPreferences />
          </div>
        </div>

        {/* Engine API Keys — self-gates on ENGINE license / existing keys; owns its column */}
        <ApiKeysSection />

        {/* Connected apps (MCP clients authorized over OAuth) — self-gates on having any; owns its
            column. Separate from Engine API Keys on purpose: those are credentials the user made by
            hand, these are app sessions granted by consent, and conflating them would invite
            revoking one from a screen that explains the other. */}
        <ConnectedAppsSection />

        {/* Shared folders: "Who can be invited". Self-gates on sharing (pilot) and owner/admin. */}
        <SharingSettingsSection />
      </div>

      {pendingSwitch && (
        <CustomModal
          title='Change linked subscription'
          closeModal={() => {
            if (!isLinkingSubscription) setPendingSwitch(null)
          }}
          buttons={[
            {
              text: 'Cancel',
              className: 'btn btn-light',
              onClick: () => setPendingSwitch(null),
              disabled: isLinkingSubscription,
            },
            {
              text: isLinkingSubscription ? 'Linking…' : 'Link',
              className: 'btn btn-primary',
              onClick: () => void handleConfirmSwitch(),
              disabled: isLinkingSubscription,
            },
          ]}
        >
          <div>
            <p className='mb-2 break-word' style={{wordBreak: 'break-all'}}>
              Link this app to <strong>{pendingSwitch.product || 'this subscription'}</strong> with key
              <span className='d-inline-block text-muted ms-1'>{pendingSwitch.subscription_id}</span>?
            </p>
            <p className='mb-0 text-muted' style={{fontSize: '0.9em'}}>
              Usage, members, and cloud units will use this linked subscription. You can choose
              another linked key here at any time.
            </p>
          </div>
        </CustomModal>
      )}
    </div>
  )
}
